// Middleware Redux que conecta Socket.IO ↔ store.
// - Escuta actions `ws/connect`, `ws/disconnect`, `ws/joinRoom`, `ws/leaveRoom`.
// - Despacha eventos do socket como Redux actions (cada feature ouve com
//   listenerMiddleware ou via slice extraReducers).
// Readme §6 frontend e §13 resiliência (reconnect backoff).
import type { Middleware } from '@reduxjs/toolkit';
import { getSocket } from './client';
import { refreshAccessToken } from '@/lib/auth/refresh';
import type { WsAction } from './types';
import { WS_NAMESPACES, type WsNamespace } from '@/config/constants';

type AnyAction = { type: string; [k: string]: unknown };

function isWsAction(action: unknown): action is WsAction {
  if (typeof action !== 'object' || action === null) return false;
  const t = (action as AnyAction).type;
  return t === 'ws/connect' || t === 'ws/disconnect' || t === 'ws/joinRoom' || t === 'ws/leaveRoom';
}

// Eventos vindos do servidor que viram actions Redux.
// Cada namespace declara seus eventos abaixo — adiciona conforme back define.
const NS_EVENTS: Record<WsNamespace, readonly string[]> = {
  inbox: [
    'message.received',
    'message.updated',
    'message.status.updated',
    'message.reactions.updated',
    'message.ai.stream',
    'conversation.created',
    'conversation.window.closed',
    'conversation.claimed',
    'conversation.released',
    'conversation.assigned',
    'conversation.status.changed',
    'conversation.read',
    'automation.toggled',
    'ai.typing',
    'ai.batching',
    'ai.suggestion',
    'presence.changed',
    'quick_replies.changed',
  ],
  production: ['order.step.changed', 'order.attachment.added'],
  system: ['feature.flag.updated', 'notification'],
};

export const wsMiddleware: Middleware = (store) => {
  const wired = new Set<WsNamespace>();

  return (next) => (action) => {
    if (!isWsAction(action)) return next(action);

    const socket = getSocket(action.namespace);

    if (!wired.has(action.namespace)) {
      wired.add(action.namespace);
      const ns = action.namespace;
      for (const evt of NS_EVENTS[ns]) {
        socket.on(evt, (payload: unknown) => {
          store.dispatch({ type: `${ns}/${evt}`, payload });
        });
      }
      socket.on('disconnect', (reason: string) => {
        store.dispatch({ type: `${ns}/disconnected`, payload: { reason } });
      });
      socket.on('connect', () => {
        store.dispatch({ type: `${ns}/connected` });
      });
      // O gateway derruba o handshake (`disconnect(true)`) quando o token está
      // vencido. Sem este listener, o cliente ficava reconectando para sempre
      // (`reconnectionAttempts: Infinity`) apresentando o MESMO token vencido — o
      // socket morria em silêncio e as conversas paravam de atualizar sozinhas.
      // Renovar o token faz a próxima tentativa levar credencial válida (o `auth`
      // do client é uma função: relê o storage a cada tentativa).
      socket.on('connect_error', (err: Error) => {
        store.dispatch({ type: `${ns}/connectError`, payload: { message: err.message } });
        void refreshAccessToken();
      });
    }

    switch (action.type) {
      case 'ws/connect':
        if (!socket.connected) socket.connect();
        break;
      case 'ws/disconnect':
        if (socket.connected) socket.disconnect();
        break;
      case 'ws/joinRoom':
        socket.emit('join', { room: action.room });
        break;
      case 'ws/leaveRoom':
        socket.emit('leave', { room: action.room });
        break;
    }

    // ESLint quer todos os ramos cobertos — usado pra evitar warning unused.
    void WS_NAMESPACES;

    return next(action);
  };
};
